import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { contractApi } from '../api';
import { CONTRACT_TYPES, CONTRACT_TYPE_COLORS, CONTRACT_STATUSES, CONTRACT_STATUS_COLORS } from '../constants';

export default function DressContracts() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [contractType, setContractType] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fetchContracts = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await contractApi.list({ q: searchQuery, contractType, status, dateFrom, dateTo, page, size: 20 });
      setContracts(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('계약 목록 조회 실패:', err);
    } finally {
      setIsLoading(false);
    }
  }, [searchQuery, contractType, status, dateFrom, dateTo, page]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">계약관리</h1>
            <p className="text-sm text-gray-600 mt-1">드레스 계약 현황을 관리합니다</p>
          </div>
          <button onClick={() => navigate('/dress-shop/contract-detail')}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-lg hover:bg-blue-700 flex items-center gap-2 cursor-pointer">
            <i className="ri-add-line"></i> 신규 계약
          </button>
        </div>

        {/* 필터 */}
        <div className="bg-white p-4 rounded-lg shadow-sm mb-6">
          <div className="grid grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">고객명</label>
              <input type="text" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }} placeholder="검색"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">계약유형</label>
              <select value={contractType} onChange={(e) => { setContractType(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">전체</option>
                {CONTRACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">상태</label>
              <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">전체</option>
                {CONTRACT_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">시작일</label>
              <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">종료일</label>
              <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>

        <div className="text-sm text-gray-600 mb-4">
          총 <span className="font-semibold text-gray-800">{total}</span>건의 계약
        </div>

        {/* 테이블 */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약번호</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">고객명</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">유형</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">계약일</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">금액</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">상태</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">담당자</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contracts.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">등록된 계약이 없습니다.</td></tr>
                  ) : contracts.map((c: any) => (
                    <tr key={c.dressContractIdx} className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/dress-shop/contract-detail?id=${c.dressContractIdx}`)}>
                      <td className="px-4 py-4 text-sm font-medium text-blue-600">{c.contractNo}</td>
                      <td className="px-4 py-4 text-sm text-gray-900">{c.dressCustomer?.name || '-'}</td>
                      <td className="px-4 py-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${CONTRACT_TYPE_COLORS[c.contractType] || 'bg-gray-100 text-gray-700'}`}>
                          {CONTRACT_TYPES.find(t => t.value === c.contractType)?.label || c.contractType}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{c.contractDt}</td>
                      <td className="px-4 py-4 text-sm font-semibold text-gray-900 text-right">{(c.finalAmount || 0).toLocaleString()}원</td>
                      <td className="px-4 py-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${CONTRACT_STATUS_COLORS[c.status] || ''}`}>
                          {CONTRACT_STATUSES.find(s => s.value === c.status)?.label || c.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{c.assignedStaff?.name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="px-3 py-2 rounded-lg text-sm border border-gray-300 disabled:opacity-50 hover:bg-gray-50">이전</button>
            {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)}
                className={`px-3 py-2 rounded-lg text-sm ${page === p ? 'bg-blue-600 text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="px-3 py-2 rounded-lg text-sm border border-gray-300 disabled:opacity-50 hover:bg-gray-50">다음</button>
          </div>
        )}
      </div>
    </div>
  );
}
